import { useContext, useEffect, useState } from 'react';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { CheckboxControlled } from 'components/UI/CheckboxControlled/CheckboxControlled';
import { InfoPopover } from 'components/UI/InfoPopover/InfoPopover';
import { ReactComponent as LogoWindcave } from 'assets/icons/svgs/logo-windcave.svg';
import { ReactComponent as IconLock } from 'assets/icons/svgs/icon-lock.svg';
import { ReactComponent as LogoAmex } from 'assets/icons/svgs/logo-amex.svg';
import { ReactComponent as LogoVisa } from 'assets/icons/svgs/logo-visa.svg';
import { ReactComponent as LogoMastercard } from 'assets/icons/svgs/logo-master.svg';
import { PaymentPageContext } from 'components/Payment/PaymentPageProvider';
import { toAudString } from 'utils/currency';
import { getCreditCardType } from '../payment-utils';
import './PaymentCardPayment.scss';

export const PaymentCardPayment = (props) => {
  const { isDonationOnly, TCText, paymentIsProcessing, formErrorMessage, formState } = props;
  const [creditCardType, setCreditCardType] = useState(null);
  const { order } = useContext(PaymentPageContext);
  const cartTotalIsGreaterThanZero = order.Cart.OrderTotal > 0;

  const getCreditCardLogo = () => {
    let logo;
    switch (creditCardType) {
      case 'visa':
        logo = <LogoVisa />;
        break;
      case 'master':
        logo = <LogoMastercard />;
        break;
      case 'amex':
        logo = <LogoAmex />;
        break;
      default:
        return false;
    }
    return logo;
  };
  const creditCardLogo = getCreditCardLogo();

  const creditCardNumberInputHandler = (inputValue) => {
    setCreditCardType(getCreditCardType(inputValue));
  };

  const orderButtonText = () => {
    let submitButtonText;
    if (!paymentIsProcessing) {
      if (order.Cart.OrderTotal > 0) {
        submitButtonText = `Pay ${toAudString(order.Cart.OrderTotal)} now`;
      } else {
        submitButtonText = 'Finalise Payment';
      }
    } else {
      submitButtonText = 'Processing...';
    }
    return submitButtonText;
  };

  /*
   * When the 'formErrorMessage' has a length then it means the
   * form has been submitted and an error has come back. In this case
   * hide the credit card validation message
   */
  useEffect(() => {
    if (formErrorMessage?.length) {
      setCreditCardType(null);
    }
  }, [formErrorMessage]);

  return (
    <>
      {cartTotalIsGreaterThanZero && (
        <div>
          <div className="card-types-wrap">
            {!isDonationOnly && (
              <p>
                <strong>or</strong>
              </p>
            )}
            <p>
              Pay <strong>{toAudString(order.Cart.OrderTotal)}</strong> via credit card
            </p>
            <ul className="card-types">
              <li className="card-visa">
                <span className="sr-only">Visa</span>
              </li>
              <li className="card-master">
                <span className="sr-only">Master Card</span>
              </li>
              <li className="card-amex">
                <span className="sr-only">American Express</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      <div className="form-payment">
        <div className="row">
          <div className="col-sm-6 col-md-4">
            {cartTotalIsGreaterThanZero && (
              <>
                <div className="form-group">
                  <div className="input-container">
                    <InputControlled
                      id="inputCardName"
                      labelText="Name on card"
                      error={formState.errors.inputCardName}
                      ariaRequired
                      showAllErrorMessages
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container">
                    <InputControlled
                      id="inputCardNo"
                      labelText="Card number"
                      error={formState.errors.inputCardNo}
                      onChangeValueHandler={creditCardNumberInputHandler}
                      maxLength={16}
                      ariaRequired
                      showAllErrorMessages
                    />
                  </div>

                  {creditCardType && !formState?.errors?.inputCardNo?.length && (
                    <div className="credit-card-type-icon">
                      {creditCardLogo && <div className="card-type">{creditCardLogo}</div>}
                      <em className="text-success">Valid card number</em>
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="col-xs-6">
                    <div className="form-group">
                      <div className="input-container">
                        <InputControlled
                          id="inputCardDate"
                          labelText="Expiry date"
                          placeholderText="MM/YYYY"
                          error={formState.errors.inputCardDate}
                          maxLength={7}
                          ariaRequired
                          showAllErrorMessages
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-xs-6">
                    <div className="form-group">
                      <div className="input-container">
                        <InputControlled
                          id="inputCardSecurity"
                          labelText="Security code"
                          error={formState.errors.inputCardSecurity}
                          maxLength={4}
                          ariaRequired
                          showAllErrorMessages
                        />
                        <InfoPopover
                          extendClass="link-popover-info security-card__popover"
                          infoContent="A 3 or 4-digit number following right after your credit card number."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className={formState.errors.checkboxTerms ? 'group-has-error' : ''}>
              <CheckboxControlled
                type="checkbox"
                id="checkboxTerms"
                labelText={parse(TCText)}
                error={formState.errors.checkboxTerms}
                ariaRequired
                extendClass="custom-checkbox checkbox-lg"
              />
            </div>

            {!paymentIsProcessing && formErrorMessage && (
              <div className="callout-row">
                <div className="callout-container callout-error">
                  <p>{formErrorMessage}</p>
                </div>
              </div>
            )}

            <button
              className="btn btn-primary btn-cta"
              type="submit"
              disabled={paymentIsProcessing}
            >
              <IconLock className="icon fill-white" />
              {orderButtonText()}
              <span />
            </button>

            <div className="payment-express">
              <a
                href="https://sec.windcave.com/pxmi3/privacy-policy"
                target="_blank"
                title="Go to the Privacy Policy page on Windcave website"
                rel="noreferrer"
              >
                <LogoWindcave
                  className="img-responsive"
                  alt="Wincave logo"
                  width="90"
                  height="32"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

PaymentCardPayment.propTypes = {
  isDonationOnly: PropTypes.bool,
  TCText: PropTypes.string,
  paymentIsProcessing: PropTypes.bool,
  formErrorMessage: PropTypes.string,
  formState: PropTypes.object,
};
